import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  KeyboardAvoidingView,
  Platform,
  SafeAreaView,
  Animated,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, fontSizes, spacing } from '../styles/theme';
import { AuroraVeilBg } from './AuroraVeilBg';
import { BrandLogo } from './BrandLogo';
import { useEntranceAnimation } from '../hooks/useEntranceAnimation';

interface AuthLayoutProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  showBackButton?: boolean;
  onBack?: () => void;
  centerContent?: boolean;
  loading?: boolean;
  /** When false, the brand logo is hidden above the title. Defaults to true. */
  showLogo?: boolean;
}

export const AuthLayout: React.FC<AuthLayoutProps> = ({
  title,
  subtitle,
  children,
  showBackButton = false,
  onBack,
  centerContent = true,
  loading = false,
  showLogo = true,
}) => {
  const logoEntrance = useEntranceAnimation({ delay: 0 });
  const titleEntrance = useEntranceAnimation({ delay: 120 });
  const bodyEntrance = useEntranceAnimation({ delay: 220 });

  return (
    <SafeAreaView style={styles.safeArea}>
      <AuroraVeilBg />
      <KeyboardAvoidingView
        style={styles.container}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        {showBackButton && (
          <View style={styles.header}>
            <TouchableOpacity
              style={styles.backButton}
              onPress={onBack}
              disabled={loading}
            >
              <Ionicons name="arrow-back" size={24} color={colors.text} />
            </TouchableOpacity>
          </View>
        )}

        <ScrollView
          contentContainerStyle={[
            styles.scrollContent,
            centerContent && styles.scrollContentCentered,
          ]}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.content}>
            {showLogo && (
              <Animated.View style={[styles.logoContainer, logoEntrance]}>
                <BrandLogo variant="horizontal" height={42} />
              </Animated.View>
            )}

            <Animated.View style={[styles.titleContainer, titleEntrance]}>
              <Text style={styles.title}>{title}</Text>
              {subtitle && (
                <Text style={styles.subtitle}>{subtitle}</Text>
              )}
            </Animated.View>

            <Animated.View style={[styles.childrenContainer, bodyEntrance]}>
              {children}
            </Animated.View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: colors.background,
  },
  container: {
    flex: 1,
  },
  header: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    flexDirection: 'row',
    alignItems: 'center',
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
    justifyContent: 'center',
    alignItems: 'center',
  },
  scrollContent: {
    flexGrow: 1,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
  },
  scrollContentCentered: {
    justifyContent: 'center',
  },
  content: {
    flex: 1,
    minHeight: '100%',
  },
  logoContainer: {
    alignItems: 'center',
    marginBottom: spacing.lg,
  },
  titleContainer: {
    alignItems: 'center',
    marginBottom: spacing.xl,
  },
  title: {
    fontSize: fontSizes['3xl'],
    fontWeight: 'bold',
    color: colors.text,            // dark text on light background
    textAlign: 'center',
    marginBottom: spacing.sm,
  },
  subtitle: {
    fontSize: fontSizes.lg,
    color: colors.textSecondary,
    textAlign: 'center',
    lineHeight: fontSizes.lg * 1.4,
  },
  childrenContainer: {
    flex: 1,
  },
});
